/**
 * reglas_compensacion.js
 * FUENTE ÚNICA DE VERDAD para el cálculo matricial de la red Duletia.
 * Este script es utilizado por el Dashboard Web (frontend) y el Webhook de n8n (backend).
 */

// Precio base de la caja — se sincroniza con BD vía sistema_config
// En el browser: window.PBOX_OVERRIDE lo setea loadSystemConfig() desde PostgreSQL
// En n8n: se pasa como parámetro o se usa el default
function _getPBOX() {
    if (typeof window !== 'undefined' && window.PBOX_OVERRIDE) return window.PBOX_OVERRIDE;
    return 4500;
}

/**
 * Calcula los activos generados, aplicando la "Regla 1:1" y los candados de Galardones.
 * 
 * @param {boolean} comproActivo - ¿El usuario hizo su compra mínima mensual?
 * @param {number} frontalesActivos - Número de líneas frontales (N1) activas este mes.
 * @param {string} galardon - Nivel de galardón del usuario ('acero', 'plata', 'oro', 'diamante', etc.)
 * @param {boolean} esEmpresarioLed - Si el usuario ya desbloqueó permanentemente la meta de $10k.
 * @param {object} cajasPorNivel - Ej: { n1: 7, n2: 9, n3: 15, n4: 10 }
 * @param {number} [precioCaja] - Precio de caja en la moneda del líder (override de _getPBOX)
 * @returns {object} { bruto, retenido, final, n1, n2, n3, n4, penalizado, nTipo, nSub }
 */
function calcularPlanDulet(comproActivo, frontalesActivos, galardon, esEmpresarioLed, cajasPorNivel, precioCaja) {
    const PBOX = precioCaja || _getPBOX();
    // REGLA: Solo cajas completas generan comisión (Math.floor) — alineado con QR
    const brutoN1 = Math.floor(cajasPorNivel.n1 || 0) * PBOX * 0.20;
    const brutoN2 = Math.floor(cajasPorNivel.n2 || 0) * PBOX * 0.30;
    const brutoN3 = Math.floor(cajasPorNivel.n3 || 0) * PBOX * 0.05;
    const brutoN4 = Math.floor(cajasPorNivel.n4 || 0) * PBOX * 0.05;
    const totalBruto = brutoN1 + brutoN2 + brutoN3 + brutoN4;

    // 1. REGLA: Activación obligatoria
    if (!comproActivo) {
        return {
            bruto: totalBruto, retenido: totalBruto, final: 0,
            n1: 0, n2: 0, n3: 0, n4: 0,
            penalizado: true,
            nTipo: 'noderecho',
            nTxto: 'SIN DERECHO A ACTIVOS',
            nSub: 'No realizó su compra personal este mes. Los activos generados por la cadena no son acreditables.'
        };
    }

    // 2. Determinar los frontales REQUERIDOS para cobrar sin topes
    let minRequerido = 0;
    let rankRequeridoStr = "";

    // Regla del Empresario LED — umbral configurable por moneda desde sistema_config
    // Nota: el dashboard pasa el precioCaja correcto por moneda y verifica el umbral LED
    // por separado. Aquí usamos el umbral MXN como fallback genérico.
    const _umbralObj = (typeof window !== 'undefined' && window.UMBRAL_EMPRESARIO_LED) || null;
    const _umbralEmpresario = (typeof _umbralObj === 'object' && _umbralObj !== null) ? (_umbralObj.MXN || 10000) : (_umbralObj || 10000);
    if (esEmpresarioLed || totalBruto >= _umbralEmpresario) {
        minRequerido = 5;
        rankRequeridoStr = "Empresario LED (5)";
    }

    // Sobreescritura estricta por Galardón
    const gal = (galardon || '').toLowerCase();
    if (gal === 'plata' || gal === 'platino') {
        minRequerido = Math.max(minRequerido, 10);
        rankRequeridoStr = "LED Plata/Platino (10)";
    } else if (gal === 'oro' || gal === 'perla' || gal === 'perla negra') {
        minRequerido = Math.max(minRequerido, 20);
        rankRequeridoStr = "LED Oro/Perla (20)";
    } else if (gal === 'diamante') {
        minRequerido = Math.max(minRequerido, 25);
        rankRequeridoStr = "LED Diamante (25)";
    }

    // 3. Verificamos si cumple con los frontales requeridos
    if (minRequerido === 0 || frontalesActivos >= minRequerido) {
        // Cobra el 100% de la red
        return {
            bruto: totalBruto, retenido: 0, final: totalBruto,
            n1: brutoN1, n2: brutoN2, n3: brutoN3, n4: brutoN4,
            penalizado: false,
            nTipo: 'ok',
            nTxto: '¡Juntos podemos lograrlo!',
            nSub: 'Tu cadena está en orden. Sigue construyendo con Dulet y alcanza el siguiente nivel. ¡Éxito!'
        };
    } else {
        // 4. PENALIZACIÓN: Se aplica la REGLA 1:1
        
        // N1 se cobra intacto
        const finalN1 = brutoN1;
        
        // N2 se topa a la "cantidad de frontales (N1) activos"
        const cajasN2Cobrables = Math.min(Math.floor(cajasPorNivel.n2 || 0), frontalesActivos);
        const finalN2 = cajasN2Cobrables * PBOX * 0.30;
        
        // N3 y N4 se pierden ($0)
        const finalN3 = 0;
        const finalN4 = 0;

        const totalFinal = finalN1 + finalN2 + finalN3 + finalN4;

        return {
            bruto: totalBruto, 
            retenido: totalBruto - totalFinal, 
            final: totalFinal,
            n1: finalN1, 
            n2: finalN2, 
            n3: finalN3, 
            n4: finalN4, 
            penalizado: true,
            nTipo: 'frontales',
            nTxto: 'ATENCIÓN: Frontales insuficientes',
            nSub: `Necesitas ${minRequerido} frontales directos para cobrar la totalidad de tu cadena (tienes ${frontalesActivos}).`
        };
    }
}

// ── BOLSA GLOBAL CON CADUCIDAD (LEDGER FIFO) ──────────────────────

/**
 * Calcula la Billetera Virtual aplicando Consumo FIFO y Caducidad de 3 meses.
 * @param {Array}  historialGenerado      - [{ mes, generado, generadoOriginal, ajusteMaestro, compro }] (más antiguo primero)
 * @param {Number} totalGastadoHistorico  - Suma histórica de débitos (fallback cuando no hay debitosPorMes)
 * @param {String} mesActual              - Mes actual 'YYYY-MM'
 * @param {Object} debitosPorMes          - { 'YYYY-MM': monto } — débitos reales por mes (preferido)
 * @param {String} mesRealExpiracion      - Mes de referencia para calcular expiración (opcional)
 *
 * FIX: Al expirar un mes, sus débitos ya NO penalizan los meses activos.
 *      Solo el saldo neto positivo (gen − deb) del mes expirado se arrastra (carryForward).
 *      Si el mes expirado tuvo déficit, ese déficit NO se hereda.
 */
if (typeof window !== 'undefined' && !window._cierreActivosConfig) {
    try {
        const _cachedCierre = JSON.parse(localStorage.getItem('dulet_cierre_activos_cfg') || 'null');
        if (_cachedCierre) window._cierreActivosConfig = _cachedCierre;
    } catch(e) {}
}

function calcularBolsaGlobal(historialGenerado, totalGastadoHistorico, mesActual, debitosPorMes, mesRealExpiracion, ultimoMesLiberado) {
    // mesRealExpiracion: si se proporciona, se usa para calcular expiración FIFO.
    // Permite que el historial sea relativo al mes del filtro pero la expiración siempre sea real.
    const mesExpiracion = mesRealExpiracion || mesActual;
    const debMes = debitosPorMes || {};
    const tenemosDebMes = Object.keys(debMes).length > 0;
    const uLib = ultimoMesLiberado || 
        (typeof window !== 'undefined' && window._cierreActivosConfig?.ultimo_mes_liberado) ||
        (typeof localStorage !== 'undefined' && (function(){
            try { return JSON.parse(localStorage.getItem('dulet_cierre_activos_cfg') || '{}').ultimo_mes_liberado; } catch(e){ return null; }
        })()) || null;

    function diffMeses(mesAnt, mesAct) {
        if (!mesAnt || !mesAct) return 999;
        const [y1, m1] = mesAnt.split('-').map(Number);
        const [y2, m2] = mesAct.split('-').map(Number);
        return (y2 - y1) * 12 + (m2 - m1);
    }

    // ── PASO 1: Inicializar cada mes en orden cronológico (cubetas) ──
    const meses = (historialGenerado || []).map(item => {
        const edadMeses = diffMeses(item.mes, mesExpiracion);
        const genOriginal = item.generadoOriginal || item.generado || 0;
        const esPosteriorALiberado = uLib && item.mes > uLib;
        const esPendiente = esPosteriorALiberado || edadMeses <= 0;

        return {
            mes: item.mes,
            generado: item.generado || 0,
            generadoOriginal: genOriginal,
            disponibleActual: item.generado || 0,
            consumidoFIFO: 0,
            expiradoLocal: 0,
            edadMeses,
            estatus: item.compro === false ? 'sin_derecho' : (esPendiente ? 'pendiente_liberacion' : 'disponible'),
            ajusteMaestro: item.ajusteMaestro || 0,
            esPendiente
        };
    });

    // ── PASO 2: Cascada FIFO Pura (Oldest-First) ──
    // Cada cobro registrado va vaciando la cubeta disponible más antigua
    if (tenemosDebMes) {
        const mesesConDebitos = Object.keys(debMes).sort();
        for (const mDeb of mesesConDebitos) {
            let remGasto = parseFloat(debMes[mDeb] || 0);
            for (const m of meses) {
                if (remGasto <= 0) break;
                // Un débito realizado en mDeb no puede gastar activos de meses posteriores
                if (m.mes > mDeb) break;
                // NUNCA consumir de un mes pendiente de liberación (corte formal pendiente)
                if (m.esPendiente) continue;
                if (m.disponibleActual > 0) {
                    const take = Math.min(remGasto, m.disponibleActual);
                    m.consumidoFIFO += take;
                    m.disponibleActual -= take;
                    remGasto -= take;
                }
            }
        }
    } else {
        // Fallback si no hay débitos por mes: drenar totalGastadoHistorico oldest-first
        let remGasto = Math.max(0, parseFloat(totalGastadoHistorico || 0));
        for (const m of meses) {
            if (remGasto <= 0) break;
            if (m.esPendiente) continue;
            if (m.disponibleActual > 0) {
                const take = Math.min(remGasto, m.disponibleActual);
                m.consumidoFIFO += take;
                m.disponibleActual -= take;
                remGasto -= take;
            }
        }
    }

    // ── PASO 3: Expiración y Estado Final ──
    // Los activos tienen vigencia de 3 meses naturales (M-1, M-2, M-3).
    // Al cumplir 4 meses de antigüedad (edadMeses >= 4):
    // SOLO expira el remanente que NO se gastó. Lo que ya se gastó, queda consumido!
    let expiradoTotal = 0;
    let saldoDisponible = 0;

    for (const m of meses) {
        if (m.edadMeses >= 4) {
            if (m.disponibleActual > 0) {
                m.expiradoLocal = m.disponibleActual;
                expiradoTotal += m.disponibleActual;
                m.disponibleActual = 0;
                m.estatus = 'expirado';
            } else {
                m.estatus = m.generadoOriginal > 0 ? 'consumido' : 'expirado';
            }
        } else if (m.esPendiente) {
            m.estatus = 'pendiente_liberacion';
        } else {
            if (m.disponibleActual === 0 && m.generadoOriginal > 0) {
                m.estatus = 'consumido';
            } else if (m.disponibleActual > 0 && m.estatus !== 'sin_derecho') {
                m.estatus = 'disponible';
                saldoDisponible += m.disponibleActual;
            }
        }
    }

    return { saldoDisponible, expiradoTotal, gastoPendiente: 0, carryForward: 0, detalleMeses: meses };
}

/**
 * Calcula la cantidad de frontales directos (Nivel 1) activos en el mes.
 * Un frontal califica como activo si y solo si la suma de cajas de todas sus
 * compras en dicho mes acumula al menos minCajas (default 1 caja completa).
 *
 * @param {Array} compras - Array de ventas/compras del mes (filas de notas)
 * @param {number} [minCajas=1] - Cajas mínimas completas para calificar (default: 1)
 * @returns {number} Número de frontales activos
 */
function contarFrontalesActivos(compras, minCajas = 1) {
    if (!Array.isArray(compras) || !compras.length) return 0;
    const cajasPorFrontal = {};
    for (let i = 0; i < compras.length; i++) {
        const v = compras[i];
        const lvl = parseInt(v.nivel ?? 0);
        if (lvl === 1) {
            const reg = v.clienteReg || v.cliente_registro || v.cliente_id;
            if (reg) {
                const c = parseFloat(v.cajas || v.cajas_calc || 0);
                cajasPorFrontal[reg] = (cajasPorFrontal[reg] || 0) + c;
            }
        }
    }
    let count = 0;
    for (const reg in cajasPorFrontal) {
        if (Math.floor(cajasPorFrontal[reg]) >= minCajas) {
            count++;
        }
    }
    return count;
}

// Expulsar si estamos en Node (Ej. n8n webhook) o dejar en Window para el navegador (Dashboard)
if (typeof window !== 'undefined') {
    window.calcularPlanDulet = calcularPlanDulet;
    window.calcularBolsaGlobal = calcularBolsaGlobal;
    window.contarFrontalesActivos = contarFrontalesActivos;
    window._getPBOX = _getPBOX;
}
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calcularPlanDulet, calcularBolsaGlobal, contarFrontalesActivos, _getPBOX };
}
